import type * as React from 'react';
import { cn } from './cn';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'card' | 'list' | 'avatar' | 'default';
  lines?: number; // only used with variant="text" or "list"
}

export function Skeleton({ className, variant = 'default', lines = 3, ...props }: SkeletonProps) {
  if (variant === 'text') {
    return (
      <div className={cn('space-y-2', className)} {...props}>
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'animate-pulse rounded bg-muted h-4',
              i === lines - 1 ? 'w-3/4' : 'w-full',
            )}
          />
        ))}
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div
        className={cn('animate-pulse rounded-xl border bg-card p-4 space-y-3', className)}
        {...props}
      >
        <div className="h-4 w-1/2 rounded bg-muted" />
        <div className="h-20 rounded bg-muted" />
        <div className="h-3 w-3/4 rounded bg-muted" />
      </div>
    );
  }

  if (variant === 'avatar') {
    return <div className={cn('animate-pulse rounded-full bg-muted', className)} {...props} />;
  }

  if (variant === 'list') {
    return (
      <div className={cn('space-y-3', className)} {...props}>
        {Array.from({ length: lines }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 animate-pulse">
            <div className="h-9 w-9 rounded-full bg-muted shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3 w-1/3 rounded bg-muted" />
              <div className="h-3 w-2/3 rounded bg-muted" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return <div className={cn('animate-pulse rounded-md bg-muted', className)} {...props} />;
}
